import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import lifetimingLogoUrl from "@/assets/LT_Full_Logo-03_no marg.png";
import { Card } from "@/shared/ui/primitives/Card";
import { cn } from "@/shared/cn";
import { fontSizeVar } from "@/shared/ui/type-scale";

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-[var(--color-bg)] px-[24px] py-[48px]"
      style={AUTH_COLOR_STYLE}
    >
      <div className="flex w-full max-w-[400px] flex-col gap-[24px]">{children}</div>
    </div>
  );
}

const AUTH_COLOR_STYLE = {
  "--color-brand": "var(--brand-orange)",
  "--color-brand-soft": "color-mix(in srgb, var(--brand-orange) 14%, transparent)",
  "--color-text-on-brand": "#ffffff",
} as CSSProperties;

export function Wordmark() {
  return (
    <div className="flex justify-center">
      <img
        src={lifetimingLogoUrl}
        alt="Lifetiming"
        className={cn(
          "h-auto w-[210px] max-w-full",
          "dark:[:root:not([data-theme=light])_&]:invert dark:[:root:not([data-theme=light])_&]:hue-rotate-180",
          "[:root[data-theme=dark]_&]:invert [:root[data-theme=dark]_&]:hue-rotate-180",
        )}
      />
    </div>
  );
}

export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Card className="shadow-[var(--shadow-soft)]">
      <div className="mb-[24px] text-center">
        <h1
          className="text-[var(--color-text-strong)]"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: fontSizeVar("lg"),
            lineHeight: 1.2,
            letterSpacing: "-0.5px",
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-[6px] text-md leading-[1.5] text-[var(--color-text-muted)]">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </Card>
  );
}

export function Field({
  id,
  label,
  className,
  trailing,
  ...rest
}: {
  id: string;
  label: string;
  trailing?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label
        htmlFor={id}
        className="text-[var(--color-text-dim)]"
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 500,
          fontSize: fontSizeVar("sm"),
          letterSpacing: "-0.01em",
        }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          className={cn(
            "w-full rounded-[var(--radius-md)] border border-[var(--color-light-01)] bg-[var(--color-paper)]",
            "px-[12px] py-[10px] text-md text-[var(--color-text-strong)]",
            "placeholder:text-[var(--color-text-faint)]",
            "transition-colors duration-[var(--duration-fast)]",
            "focus:outline-none focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[var(--color-brand-soft)]",
            Boolean(trailing) && "pr-[42px]",
            className,
          )}
          style={{ fontFamily: "var(--font-display)" }}
          {...rest}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-[6px]">{trailing}</div>
        )}
      </div>
    </div>
  );
}

export function PrimaryButton({
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      className={cn(
        "w-full rounded-[var(--radius-md)] bg-[var(--color-brand)] px-[16px] py-[10px]",
        "text-md font-medium text-[var(--color-text-on-brand)]",
        "transition-opacity duration-[var(--duration-fast)] hover:opacity-90",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)]",
        className,
      )}
      style={{ fontFamily: "var(--font-display)" }}
      {...(rest as object)}
    >
      {children}
    </button>
  );
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-[var(--radius-md)] bg-[var(--color-withdrawal-soft)] px-[12px] py-[8px] text-md text-[var(--color-withdrawal)]"
      style={{ fontFamily: "var(--font-display)" }}
    >
      {children}
    </div>
  );
}
